import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import EdpiesLogo from '@/components/EdpiesLogo';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 py-4 px-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="text-slate-300 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div onClick={() => navigate('/')} className="cursor-pointer">
            <EdpiesLogo size="small" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl md:text-4xl font-bold mb-8 text-center bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">
          Privacy Policy
        </h1>
        
        <div className="prose prose-invert prose-slate max-w-none space-y-8">
          <p className="text-slate-300 text-lg">
            Last updated: February 2025
          </p>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">1. Introduction</h2>
            <p className="text-slate-300">
              Welcome to Edpies ("we," "our," or "us"). We are committed to protecting your privacy and the privacy of children who use our educational platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">2. Information We Collect</h2>
            <p className="text-slate-300">We collect information that you provide directly to us, including:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2">
              <li><strong>Account Information:</strong> Name, email address, password, and user type (student, parent, or tutor)</li>
              <li><strong>Student Profile:</strong> Year group, subjects of interest, learning goals, and learning preferences</li>
              <li><strong>Learning Data:</strong> Practice session results, diagnostic assessments, progress tracking, and capability bands</li>
              <li><strong>Communication Data:</strong> Feedback submissions and support requests</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">3. How We Use Your Information</h2>
            <p className="text-slate-300">We use the information we collect to:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2">
              <li>Provide, maintain, and improve our educational services</li>
              <li>Personalise learning experiences and generate tailored study plans</li>
              <li>Track progress and provide insights to parents and tutors</li>
              <li>Communicate with you about your account and our services</li>
              <li>Ensure the safety and security of our platform</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">4. Children's Privacy</h2>
            <p className="text-slate-300">
              We are committed to protecting the privacy of children. Our platform is designed for use by children under parental supervision. We require parental consent for children under 13 to create accounts, and parents can access, modify, or delete their child's information at any time through their parent dashboard.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">5. Data Sharing</h2>
            <p className="text-slate-300">We do not sell your personal information. We may share information with:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2">
              <li><strong>Parents/Guardians:</strong> Progress and performance data for linked students</li>
              <li><strong>Tutors:</strong> Relevant learning data for students they are working with</li>
              <li><strong>Service Providers:</strong> Third-party services that help us operate our platform</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">6. Data Security</h2>
            <p className="text-slate-300">
              We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. This includes encryption, secure servers, and regular security assessments.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">7. Your Rights</h2>
            <p className="text-slate-300">You have the right to:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to certain processing activities</li>
              <li>Export your data in a portable format</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">8. Cookies</h2>
            <p className="text-slate-300">
              We use cookies and similar technologies to enhance your experience, analyse usage, and personalise content. You can control cookie preferences through your browser settings.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">9. Changes to This Policy</h2>
            <p className="text-slate-300">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">10. Contact Us</h2>
            <p className="text-slate-300">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p className="text-slate-300">
              Email: privacy@edpies.com
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-slate-800 py-6 px-4 text-center text-slate-500">
        <p>© 2025 Edpies. All rights reserved.</p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
